import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { FormattedNote } from 'components/common';
import type { ApplicationDetailsFormatted } from 'queries/applications/useApplicationByReferenceNumber/types';
import { useBtlApplicationInProgressById, useConveyancing } from 'queries/btlApplications';
import { useCaseTasks } from 'queries/brokerCases/useCaseTasks';
import { usePrefetchDocumentReviews } from 'queries/brokerCases';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { notesApi } from 'api/notes';
import { useIsOoCaseNewSectionsEnabled } from 'providers/PosthogProvider';

export function useOoApplicationViewData(application: ApplicationDetailsFormatted) {
  const caseId = String(application.id);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: conveyancing, isLoading: isConveyancingLoading } = useConveyancing(caseId);
  const { data: caseTasksData, isLoading: isCaseTasksLoading } = useCaseTasks(caseId);
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const isNewSectionsEnabled = useIsOoCaseNewSectionsEnabled();

  const sections = caseTasksData?.sections ?? [];
  const bankAccount = caseData?.borrower?.bankAccount;
  const valuation = application.valuation;

  const sidebarContext = useMemo<OoCaseSidebarContext>(
    () => ({
      status: application.status,
      hasValuation: Boolean(valuation),
      hasConveyancing: !isConveyancingLoading && Boolean(conveyancing),
      isNewSectionsEnabled,
    }),
    [application.status, conveyancing, isConveyancingLoading, isNewSectionsEnabled, valuation],
  );

  const formattedNotes: FormattedNote[] = useMemo(() => {
    if (!application.notes) {
      return [];
    }

    return application.notes.map(note => ({
      id: note.id,
      title: note.title,
      content: note.content,
      createdAt: note.createdAt,
      creator: note.createdBy,
    }));
  }, [application.notes]);

  const { mutateAsync: createNote, isLoading: isCreatingNote } = useMutation(
    ({ title, content }: { title: string; content: string }) =>
      notesApi.createBtlNote(caseId, { title, content, isInternal: false }),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.note.create.success' }), { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['analystApplications'] });
      },
      onError: (error: Error) => {
        enqueueSnackbar(error.message || 'Failed to create note', {
          variant: 'error',
        });
      },
    },
  );

  const handleCreateNote = async (title: string, content: string) => {
    await createNote({ title, content });
  };

  usePrefetchDocumentReviews(caseId, sections);

  return {
    caseId,
    case: caseData,
    valuation,
    sections,
    applicationFilesInternal: application.files?.internal,
    isBankDetailsComplete: Boolean(bankAccount?.accountName),
    bankAccount,
    isCaseLoading,
    isCaseTasksLoading,
    isConveyancingLoading,
    conveyancing,
    sidebarContext,
    formattedNotes,
    onCreateNote: handleCreateNote,
    isCreatingNote,
  };
}
